import { CHANNEL_NAME, type CoordMessage, isCoordMessage, type MessageMeta } from "./messages";
import { getTabId } from "./tabId";

/**
 * Single BroadcastChannel per page load. Same-origin only; both the opener and
 * the viewer window connect to the same named channel.
 *
 * Created defensively: `BroadcastChannel` is undefined in a handful of old
 * browsers (and some test/SSR contexts). Guarding here keeps this module — which
 * the Review page now imports via the broadcaster — from throwing at import
 * time; the coordination functions degrade to no-ops and the rest of the app
 * (including the Review page) renders normally.
 */
const channel = typeof BroadcastChannel === "undefined" ? undefined : new BroadcastChannel(CHANNEL_NAME);

/**
 * Distributive `Omit`: applies `Omit<T, K>` to each member of a union
 * separately (via the `T extends unknown ? ... : never` conditional-type
 * trick), instead of the built-in `Omit`'s non-distributive behavior, which
 * would otherwise collapse a discriminated union like `CoordMessage` down to
 * only the properties common to every member.
 */
type DistributiveOmit<T, K extends keyof T> = T extends unknown ? Omit<T, K> : never;

/** A coordination message before `sendCoord` stamps its `meta`. */
type CoordMessageInput = DistributiveOmit<CoordMessage, "meta"> & { meta?: Partial<MessageMeta> };

/**
 * Broadcasts a coordination message, auto-stamping `tabId`/`sentAt`/`msgId`.
 * Ported from `dsc-dual-window-poc` `src/coord/channel.ts`.
 * @param {CoordMessageInput} message - The message to send (without full `meta`).
 * @returns {CoordMessage} The fully-stamped message that was posted.
 */
export function sendCoord(message: CoordMessageInput): CoordMessage {
  const full = {
    ...message,
    meta: {
      tabId: getTabId(),
      sentAt: Date.now(),
      msgId: crypto.randomUUID(),
      ...message.meta,
    },
  } as CoordMessage;
  channel?.postMessage(full);
  return full;
}

/**
 * Subscribes to coordination messages from OTHER windows. Messages sent by the
 * current window (same `tabId`) are ignored so a broadcaster never reacts to its
 * own emits.
 * @param {(message: CoordMessage) => void} callback - Invoked per foreign message.
 * @returns {() => void} Unsubscribe function.
 */
export function subscribeCoord(callback: (message: CoordMessage) => void): () => void {
  if (!channel) {
    return () => {};
  }
  const activeChannel = channel;
  const handler = (event: MessageEvent) => {
    if (isCoordMessage(event.data) && event.data.meta.tabId !== getTabId()) {
      callback(event.data);
    }
  };
  activeChannel.addEventListener("message", handler);
  return () => activeChannel.removeEventListener("message", handler);
}
